<template>
  <div
    ref="wagersTopRef"
    class="wagers-view"
  >
    <SectionHeader
      :title="$t('wagers.title')"
      variant="complex"
      class="wagers-section-header"
    >
      <template
        v-if="isAuthenticated"
        #right
      >
        <WagerStatusFilters
          :model-value="wagerStatusFilter"
          :counts="transactionsStore.statusCounts"
          @update:model-value="onWagerStatusFilterChange"
        />
      </template>
    </SectionHeader>
    <div
      v-if="!isAuthenticated"
      class="auth-error"
    >
      <p>
        {{ $t('auth.authenticationRequired') }} <a
          href="#"
          class="login-link"
          @click.prevent="handleLoginRequest"
        >{{ $t('auth.pleaseLoginLink') }}</a>.
      </p>
    </div>

    <TransactionsTable 
      v-else
      :transactions="transactionsStore.transactions"
      :loading="transactionsStore.loading"
      @view-details="viewDetails"
      @rebet="handleRebet"
      @copy-success="onCopySuccess"
      @copy-failed="onCopyFailed"
    />

    <BaseModal
      :is-open="isDetailsModalOpen"
      :title="$t('wagers.details')"
      full-screen
      hide-footer
      flush-body
      @close="closeDetailsModal"
    >
      <TransactionDetails
        v-if="transactionsStore.currentTransaction"
        :transaction="transactionsStore.currentTransaction"
        :is-rebetting="isRebetting"
        @rebet="handleRebet"
        @close="closeDetailsModal"
      />
    </BaseModal>

    <div
      v-if="transactionsStore.error"
      class="error-message"
    >
      {{ transactionsStore.error }}
    </div>

    <div
      v-if="isAuthenticated && hasWagerHistory"
      class="pagination-controls"
    >
      <BaseButton 
        v-if="currentPage > 1"
        variant="outline"
        class="pagination-button pagination-button-prev"
        :disabled="transactionsStore.loading" 
        @click="goToPage(currentPage - 1)"
      >
        {{ $t('common.previous') }}
      </BaseButton>
      <span>{{ $t('common.page') }} {{ currentPage }} {{ $t('common.of') }} {{ totalPages }}</span>
      <BaseButton 
        v-if="currentPage < totalPages"
        variant="outline"
        class="pagination-button pagination-button-next"
        :disabled="transactionsStore.loading" 
        @click="goToPage(currentPage + 1)"
      >
        {{ $t('common.next') }}
      </BaseButton>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed, watch, nextTick } from 'vue'
import { useTransactionsStore } from '@/stores/transactions'
import { useAuthStore } from '@/stores/auth'
import { useLoaderStore } from '@/stores/loader'
import { useToast } from '@/composables/useToast'
import { useI18n } from 'vue-i18n'
import TransactionsTable from '@/components/wagers/TransactionsTable.vue'
import type { Transaction } from '@/types/transaction'
import BaseButton from '@/components/BaseButton.vue'
import { useRoute } from 'vue-router'
import { iframeCommunication } from '@/utils/iframeCommunication'
import BaseModal from '@/components/BaseModal.vue'
import TransactionDetails from '@/components/wagers/TransactionDetails.vue'
import SectionHeader from '@/components/base/SectionHeader.vue'
import WagerStatusFilters from '@/components/wagers/WagerStatusFilters.vue'
import type { WagerStatusFilter } from '@/stores/transactions'
import { useRebetFromTransaction } from '@/composables/useRebetFromTransaction'

const transactionsStore = useTransactionsStore()
const authStore = useAuthStore()
const loaderStore = useLoaderStore()
const { showToast } = useToast()
const { t } = useI18n()

const onCopySuccess = () => {
  showToast({
    title: t('transactionDetails.textCopied'),
    message: '',
    type: 'success',
    duration: 2500,
    centered: true,
  })
}

const onCopyFailed = () => {
  showToast({
    title: t('common.error'),
    message: t('transactionDetails.copyFailed'),
    type: 'error',
    duration: 4000,
    centered: true,
  })
}

const { applyRebetFromCurrentTransaction, isRebetting } = useRebetFromTransaction()
const route = useRoute()
const isDetailsModalOpen = ref(false)
const savedScrollPosition = ref(0)
const wagersTopRef = ref<HTMLElement | null>(null)

// Get the expand reference from query params (for auto-opening modal)
const expandReference = ref<string | null>(route.query.expand ? (route.query.expand as string) : null)

const isAuthenticated = computed(() => authStore.isAuthenticated)
const currentPage = computed(() => Number(transactionsStore.pagination.current_page) || 1)
const totalPages = computed(() => Number(transactionsStore.pagination.total_pages) || 1)
const hasWagerHistory = computed(() => {
  return transactionsStore.transactions.length > 0 || Number(transactionsStore.pagination.total_count) > 0
})

const wagerStatusFilter = computed(
  () => transactionsStore.filters.wagerStatus ?? 'all'
)

const onWagerStatusFilterChange = async (value: WagerStatusFilter) => {
  if (transactionsStore.loading || value === wagerStatusFilter.value) {
    return
  }

  transactionsStore.setWagerStatusFilter(value)
  transactionsStore.pagination.current_page = 1
  scrollWagersViewToTop()
  loaderStore.show(t('wagers.loadingTransactions'))

  try {
    await transactionsStore.fetchTransactions(1)
  } finally {
    loaderStore.hide()
  }

  await scrollWagersViewToTopAfterLoad()
}

const handleLoginRequest = () => {
  iframeCommunication.sendLoginRequest('wagers-view-required')
  return;
}

// Watch for transactions to be loaded and auto-expand if needed
watch(
  () => transactionsStore.transactions,
  (transactions) => {
    if (transactions && transactions.length > 0 && expandReference.value && !isDetailsModalOpen.value) {
      const transaction = transactions.find(t => t.reference === expandReference.value)
      if (transaction) {
        viewDetails(transaction)
      }
    }
  },
  { immediate: true }
)

const scrollWagersViewToTop = (smooth = true) => {
  const behavior = smooth ? 'smooth' : 'auto'

  if (document.activeElement instanceof HTMLElement) {
    document.activeElement.blur()
  }

  wagersTopRef.value?.scrollIntoView({ behavior, block: 'start' })

  if (iframeCommunication.isRunningInIframe()) {
    iframeCommunication.sendScrollToTop()
    return
  }

  window.scrollTo({ top: 0, behavior })
}

const scrollWagersViewToTopAfterLoad = async () => {
  await nextTick()
  requestAnimationFrame(() => {
    scrollWagersViewToTop(true)
    if (iframeCommunication.isRunningInIframe()) {
      window.setTimeout(() => scrollWagersViewToTop(true), 350)
    }
  })
}

const viewDetails = async (wager: Transaction) => {
  try {
    savedScrollPosition.value = window.pageYOffset || document.documentElement.scrollTop
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur()
    }
    loaderStore.show(t('wagers.loadingDetails'))
    await transactionsStore.fetchTransactionDetail(wager.reference)
    isDetailsModalOpen.value = true
  } catch (err) {
    isDetailsModalOpen.value = false
  } finally {
    loaderStore.hide()
  }
}

const closeDetailsModal = () => {
  isDetailsModalOpen.value = false
  nextTick(() => {
    window.scrollTo({
      top: savedScrollPosition.value,
      behavior: 'auto'
    })
  })
}

const goToPage = async (page: number) => {
  if (transactionsStore.loading) return

  scrollWagersViewToTop()
  loaderStore.show(t('wagers.loadingTransactions'))

  transactionsStore.pagination.current_page = page
  try {
    await transactionsStore.fetchTransactions(page)
  } finally {
    loaderStore.hide()
  }

  await scrollWagersViewToTopAfterLoad()
}

const handleRebet = () =>
  applyRebetFromCurrentTransaction({
    onComplete: () => {
      isDetailsModalOpen.value = false
    },
    openBetslip: true,
  })

onMounted(async () => {
  try {
    // Iframe embeds always pass `account` (tenant/theme); that is not user login.
    // Only load wagers when we have a token (URL or store) or a full session pair.
    const hasTokenInUrl = !!route.query.token
    const canLoadWagers =
      hasTokenInUrl || authStore.isAuthenticated || authStore.hasValidSession()

    if (!canLoadWagers) {
      return
    }
    
    // const authSuccess = await authStore.authenticate()
    
    // if (!authSuccess) {
    //   showToast({
    //     title: 'Authentication Failed',
    //     message: 'Invalid authentication parameters. Please check your token and account.',
    //     type: 'error',
    //     duration: 8000
    //   })
    //   return
    // }
    
    await transactionsStore.fetchTransactions()
    
    // Check if there's an expand query parameter to auto-expand a transaction
    if (expandReference.value && !isDetailsModalOpen.value) {
      // Find the transaction with this reference
      const transaction = transactionsStore.transactions.find(t => t.reference === expandReference.value)
      if (transaction) {
        // Auto-expand this transaction
        await viewDetails(transaction)
      }
    }
  } catch (err: any) {
    console.log(err)
    
    // Check if it's a 401 error (authentication failed)
    // if (err?.response?.status === 401) {
      // Clear invalid credentials
      // authStore.clearInvalidCredentials()
      // showToast({
      //   title: 'Authentication Failed',
      //   message: 'Invalid token or account. Please check your credentials.',
      //   type: 'error',
      //   duration: 8000
      // })
      // Don't return here - let the component render the auth error message
    // } else {
    //   showToast({
    //     title: 'Error',
    //     message: 'Failed to load wagers. Please try again.',
    //     type: 'error'
    //   })
    // }
  } finally {
    loaderStore.hide()
  }
})
</script>

<style scoped>
.wagers-view {
  min-height: 100%; /* Changed from 100vh to prevent iframe infinite loop */
  background: var(--background);
  color: var(--colour-base-text-colour);
  /* padding: var(--wagers-page-padding, 5px); */
  width: 100%;
  box-sizing: border-box;
}

.wagers-view :deep(.wagers-section-header.section-header.complex) {
  width: 100%;
  padding: 12px 16px 16px;
  margin-bottom: 0;
  align-items: center;
  gap: 16px;
  border-bottom: none;
}

.wagers-view :deep(.wagers-section-header .title) {
  font-size: 22px;
  font-weight: 700;
  white-space: nowrap;
}

@media (max-width: 768px) {
  .wagers-view :deep(.wagers-section-header.section-header.complex) {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    padding: 8px 10px 10px;
    gap: 8px;
  }

  .wagers-view :deep(.wagers-section-header .header-left) {
    flex: 0 0 auto;
    min-width: 0;
  }

  .wagers-view :deep(.wagers-section-header .title) {
    font-size: 20px;
  }

  .wagers-view :deep(.wagers-section-header .header-right) {
    width: auto;
    flex: 1 1 auto;
    min-width: 0;
    margin-top: 0;
    justify-content: flex-end;
    overflow: hidden;
  }
}

/* Fix for infinite resize loop in iframe */
:global(body.iframe-mode) .wagers-view {
  min-height: auto !important;
  height: auto !important;
  margin: 0 !important;
  padding-bottom: 20px; 
}

/* Fix for infinite resize loop in iframe */
:global(body.iframe-mode) .wagers-view {
  min-height: auto !important;
  height: auto !important;
}

.view-header {
  background: var(--colour-surface-cont-lowest);
  padding: 16px 24px;
  margin-bottom: 1px;
}

.view-header h1 {
  color: var(--colour-base-text-colour);
  font-size: 18px;
  font-weight: 500;
  margin: 0;
}

/* Shared button styles */
.pagination-button,
.pagination-controls button {
  min-width: 116px;
  min-height: 58px;
  padding: 0.75rem 1.5rem;
  background: var(--wagers-pagination-button-bg, var(--colour-orange-button));
  color: var(--wagers-pagination-button-color, var(--colour-icons));
  border: 2px solid var(--wagers-pagination-button-border, var(--wagers-pagination-button-bg, var(--colour-orange-button)));
  border-radius: 6px;
  cursor: pointer;
  font-size: 1rem;
  font-weight: 700;
  line-height: 1;
  transition: background-color 0.2s, color 0.2s, border-color 0.2s;
}

.pagination-button-prev,
.pagination-controls .pagination-button-prev {
  background: var(--colour-orange-button);
  color: var(--text-button);
  border-color: var(--colour-orange-button);
}

.pagination-button-next,
.pagination-controls .pagination-button-next {
  background: var(--colour-orange-button);
  color: var(--text-button);
  border-color: var(--colour-orange-button);
}

.pagination-button:hover:not(:disabled),
.pagination-controls button:hover:not(:disabled) {
  background: var(--primary-hover);
}

.pagination-button-prev:hover:not(:disabled),
.pagination-controls .pagination-button-prev:hover:not(:disabled) {
  background: var(--primary-hover);
  color: var(--text-button);
  border-color: var(--primary-hover);
}

.pagination-button-next:hover:not(:disabled),
.pagination-controls .pagination-button-next:hover:not(:disabled) {
  background: var(--primary-hover);
  color: var(--text-button);
  border-color: var(--primary-hover);
}

.pagination-controls button:disabled {
  background: var(--colour-surface-cont);
  border-color: var(--colour-surface-cont);
  color: var(--colour-secondary-text-colour);
  cursor: not-allowed;
}

/* Shared error message styles */
.auth-error,
.error-message {
  background: var(--wagers-notice-bg, color-mix(in srgb, var(--colour-red-text-colour) 12%, transparent));
  color: var(--colour-red-text-colour);
  margin: 16px;
  border-radius: var(--wagers-notice-radius, 8px);
  text-align: center;
  border: 1px solid var(--colour-surface-cont);
}

.auth-error {
  padding: 24px;
}

.auth-error p {
  margin: 8px 0;
  font-size: 14px;
}

.auth-error p:first-child {
  font-weight: 500;
  font-size: 16px;
}

.login-link {
  color: var(--colour-orange-button);
  text-decoration: underline;
  cursor: pointer;
  transition: opacity 0.2s;
}

.login-link:hover {
  opacity: 0.8;
}

.error-message {
  padding: 16px;
  border-radius: 4px;
}

.pagination-controls {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1.5rem;
  margin: 2rem 0;
}

.pagination-controls span {
  font-size: 1rem;
  font-weight: 500;
  color: var(--colour-base-text-colour);
  white-space: nowrap;
}

@media (max-width: 768px) {
  .view-header {
    padding: 16px;
  }

  .view-header h1 {
    font-size: 20px;
  }
  
  .auth-error,
  .error-message {
    margin: 8px;
    padding: 16px;
  }
  
  .pagination-controls {
    display: grid;
    grid-template-columns: 1fr auto 1fr;
    gap: 0.5rem;
    margin: 1rem 16px;
    padding: 0;
    align-items: center;
  }
  
  .pagination-button {
    min-width: 104px;
    min-height: 48px;
    padding: 8px 16px;
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    border-radius: 6px;
    text-align: center;
    justify-self: center;
    min-width: 0;
    width: 100%;
    max-width: 120px;
  }
  
  .pagination-controls span {
    text-align: center;
    font-size: 12px;
    font-weight: 500;
    color: var(--colour-secondary-text-colour);
    white-space: nowrap;
    grid-column: 2;
  }
}
</style>
